import * as React from 'react'
import { useRouter } from 'next/router'
import { LinkButton, PrimaryBlack, PrimaryYellow, SecondaryButton } from './ButtonAsync.style'
import analytics from '../../../util/analytics-data-attributes/analyticsAttributes'
import type { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'

enum Variants {
  primaryYellow = 'primary-yellow',
  primaryBlack = 'primary-black',
  secondary = 'secondary',
  link = 'link'
}

enum Sizes {
  medium = 'medium',
  large = 'large',
}

type Props = {
  children: React.ReactNode,
  onClick: React.MouseEventHandler<HTMLButtonElement>,
  isInProgress: boolean,
  variant?: Variants,
  size?: Sizes,
  disabled?: boolean,
  className?: string,
  dataAnalyticsAction?: Action,
  dataAnalyticsCategory?: Category,
  dataAnalyticsLabel?: string,
}

/**
 * Button element
 * @param {object} props Component props
 * @param {object|string} props.children The content of the button
 * @param {function} props.onClick Button action received and is in progress
 * @param {function} props.isInProgress Button action received and is in progress
 * @param {string} props.variant Button variant, primary, secondary, tertiary
 * @param {string} props.size Button medium, large
 * @param {boolean} props.disabled Button disabled state
 * @param {string} props.dataAnalyticsAction The action to be logged in analytics
 * @param {string} props.dataAnalyticsCategory The category to be logged in analytics
 * @param {string} props.dataAnalyticsLabel The label to be logged in analytics
 * @param {object} props.rest Additional optional attributes to be passed to button
 */

const Button = ({
  children,
  onClick,
  isInProgress,
  variant,
  size,
  disabled,
  className,
  dataAnalyticsAction,
  dataAnalyticsCategory,
  dataAnalyticsLabel,
  ...rest
}: Props) => {
  const styleProps = {
    onClick,
    disabled,
    size,
    ...analytics({ 
      router: useRouter(),
      action: dataAnalyticsAction,
      category: dataAnalyticsCategory,
      label: dataAnalyticsLabel
    }),
    className,
    ...rest
  }

  const spinner = isInProgress && <span className="circularProgress-root" role="progressbar">
      <svg
        className="circularProgress-svg" viewBox="22 22 44 44">
        <circle
          className="circularProgress-circle"
          cx="44"
          cy="44"
          r="20.2"
          fill="none"
          strokeWidth="3.6"
        />
      </svg>
  </span>

  switch (variant) {
    case Button.Variants.secondary:
      return <SecondaryButton {...styleProps}>{children} {spinner}</SecondaryButton>

    case Button.Variants.link:
      return <LinkButton {...styleProps}>{children} {spinner}</LinkButton>

    case Button.Variants.primaryBlack:
      return <PrimaryBlack {...styleProps}>{children} {spinner}</PrimaryBlack>

    case Button.Variants.primaryYellow:
    default:
      return <PrimaryYellow {...styleProps}>{children} {spinner}</PrimaryYellow>
  }
}

Button.Variants = Variants

Button.Sizes = Sizes

Button.defaultProps = {
  className: undefined,
  dataAnalyticsAction: undefined,
  dataAnalyticsCategory: undefined,
  dataAnalyticsLabel: undefined,
  disabled: false,
  size: Sizes.medium,
  variant: Variants.primaryYellow,
}

export default Button
